import { useEffect, useRef } from "react";
import { Spinner } from "@/components/ui/spinner";
import { TriangleAlertIcon } from "lucide-react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import StartCampaign from "@/components/dashboard_campaigns/start-campaign-dialog";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCampaignStore, useCreatorsStore, useDashboardStore } from "@/stores";
import { creatorRefreshPhase, creatorRefreshProgress } from "@/lib/creatorRefresh";
import type { CreatorRefresh } from "@/types/types";
import { StartCampaignActionProps } from "../types";
import { TextShimmer } from "@/components/loading-ui/text-shimmer";

/** How often the record is re-read while a refresh / launch is in flight. */
const REFRESH_POLL_MS = 4000;

export default function StartCampaignAction({
  data,
  campaignId,
  outreachLoading,
  onStartOutreach,
}: StartCampaignActionProps) {
  // The campaigns LIST carries creator_refresh. The launch marks it running
  // at once (CreatorsTable.handleStartInitialOutreach); from there this
  // component polls the real record until the launch outcome is stamped.
  const { campaigns, fetchCampaigns } = useDashboardStore();
  const { fetchCreators } = useCreatorsStore();
  const { refreshCampaign } = useCampaignStore();
  const { getToken, orgId } = useAuth();
  const listed = campaigns?.find((c: { _id: string }) => c._id === campaignId) as
    | { creator_refresh?: CreatorRefresh | null }
    | undefined;
  const refresh = listed?.creator_refresh ?? null;
  const phase = creatorRefreshPhase(refresh);

  useEffect(() => {
    if (!phase || !orgId) return;
    const interval = setInterval(() => {
      void (async () => {
        const token = await getToken();
        if (token) await fetchCampaigns(orgId, token);
      })();
    }, REFRESH_POLL_MS);
    return () => clearInterval(interval);
  }, [phase, orgId, getToken, fetchCampaigns]);

  // The worker stamps launched_at / launch_error when it is done. React to
  // that outcome itself, not to having watched the run: a refresh with
  // nothing to scrape ends before the page ever renders it as running.
  const launchError = refresh?.launch_error ?? null;
  const outcome = refresh?.launched_at ?? launchError;
  const seenOutcome = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (seenOutcome.current === undefined) {
      seenOutcome.current = outcome; // first render: remember, don't react
      return;
    }
    if (outcome === seenOutcome.current) return;
    seenOutcome.current = outcome;
    if (!outcome) return; // a new run just started
    void (async () => {
      const token = await getToken();
      if (!token) return;
      await fetchCreators(campaignId, token);
      if (orgId) await refreshCampaign(campaignId, orgId, token);
    })();
    if (launchError) {
      toast.error(`The campaign could not be launched: ${launchError}`);
    }
  }, [outcome, launchError, campaignId, orgId, fetchCreators, refreshCampaign, getToken]);

  const shouldShow =
    campaignId &&
    !data.some(
      (creator) =>
        creator.status !== "pending" && creator.status !== "to-contact"
    );

  if (!shouldShow) return null;

  if (phase) {
    const progress = creatorRefreshProgress(refresh);
    return (
      <Tooltip>
        <TooltipTrigger className="mx-1.5 ml-auto self-center">
          <Badge className="text-xs gap-1 bg-transparent text-muted-foreground">
            <Spinner className="size-3" />
            <TextShimmer>
              {phase === "launching" ? (
                "Launching campaign"
              ) : (
                <>
                  Refreshing creator data <span className="font-mono ml-1">{progress ?? ""}</span>
                </>
              )}
            </TextShimmer>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-64 text-center">
            Followers and views (average and median) are being re-scraped. The campaign launches
            as soon as it finishes.
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      {/* The worker refreshed but could not call the backend to launch: the
          roster is still pending, so say why and let them press Launch again. */}
      {launchError && (
        <Tooltip>
          <TooltipTrigger className="ml-auto self-center">
            <Badge variant="warning">
              <TriangleAlertIcon className="size-3" />
              Launch after refresh failed
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-72 text-center">
              Creator data was refreshed, but the campaign could not be
              launched: {launchError}. Press Launch again.
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      <StartCampaign
        allCreatorsPending={!data.every((row) => row.status === "pending")}
        outreachLoading={outreachLoading}
        handleStartInitialOutreach={onStartOutreach}
        campaignId={campaignId}
      />
    </>
  );
}
